export const supportedLanguages = [
  'en',
  'de',
  'es',
  'fr',
  'pt',
  'zh-TW',
  'it',
  'id',
  'th',
  'ar',
  'kr',
  'jp',
] as const

export type SupportedLanguage = typeof supportedLanguages[number]

const languageMap: Record<string, SupportedLanguage> = {
  en: 'en',
  'en-au': 'en',
  'en-gb': 'en',
  'en-uk': 'en',
  'en-us': 'en',
  de: 'de',
  es: 'es',
  fr: 'fr',
  pt: 'pt',
  'pt-br': 'pt',
  'pt-pt': 'pt',
  'zh-tw': 'zh-TW',
  'zh-hant': 'zh-TW',
  'zh-hk': 'zh-TW',
  'zh-mo': 'zh-TW',
  it: 'it',
  id: 'id',
  in: 'id',
  th: 'th',
  ar: 'ar',
  ja: 'jp',
  'ja-jp': 'jp',
  jp: 'jp',
  ko: 'kr',
  'ko-kr': 'kr',
  kr: 'kr',
}

const normalizeLanguage = (
  language?: string | null
): SupportedLanguage | null => {
  if (!language) return null

  const normalizedLanguage = language.trim().toLowerCase()

  if (normalizedLanguage.startsWith('zh-hant-')) return 'zh-TW'

  return (
    languageMap[normalizedLanguage] ||
    languageMap[normalizedLanguage.split('-')[0]] ||
    null
  )
}

const getStoredLanguage = (): SupportedLanguage | null =>
  normalizeLanguage(localStorage.getItem('language'))

const getBrowserLanguage = (): SupportedLanguage | null => {
  const browserLanguages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language]

  for (const language of browserLanguages) {
    const supportedLanguage = normalizeLanguage(language)
    if (supportedLanguage) return supportedLanguage
  }

  return null
}

const getDocumentLanguage = (language: SupportedLanguage): string => {
  switch (language) {
    case 'kr':
      return 'ko'
    case 'jp':
      return 'ja'
    case 'es':
      return 'es'
    case 'de':
      return 'de'
    case 'fr':
      return 'fr'
    case 'pt':
      return 'pt'
    case 'zh-TW':
      return 'zh-Hant'
    case 'it':
      return 'it'
    case 'id':
      return 'id'
    case 'th':
      return 'th'
    case 'ar':
      return 'ar'
    default:
      return 'en'
  }
}

const resolveInitialLanguage = (): SupportedLanguage =>
  getStoredLanguage() || getBrowserLanguage() || 'en'

export {
  getBrowserLanguage,
  getDocumentLanguage,
  getStoredLanguage,
  normalizeLanguage,
  resolveInitialLanguage,
}
